import { useCallback, useMemo } from 'react';
import {
  collectDocDefaultsAsInherited,
  collectHigherScopeNames,
  collectInheritedVariables,
  collectScopedVariables,
  DOC_VARIABLE_SOURCE_NAME,
  MISSION_VARIABLE_SOURCE_NAME,
  resolveVariableExpanded,
} from '../../../../utils/variableScope';

export default function useScopedVariableData({
  hasDocScope,
  hasCampaignScope,
  docVariables,
  docRows,
  campaignRows,
  campaign,
  allSprints,
  missionVariables = null,
  hasMissionScope = false,
}) {
  // Read-only context rows:
  // - In-doc table with a doc writer: ancestors only (doc rows are editable)
  // - Campaign modal (no doc writer): ancestors + unclaimed doc defaults
  // - Doc table without a campaign writer: whole campaign chain
  const inheritedRows = useMemo(() => {
    if (!campaign) return [];
    if (hasCampaignScope) {
      const ancestorRows = collectInheritedVariables(campaign, allSprints);
      if (hasDocScope) return ancestorRows;
      const taken = [
        ...ancestorRows.map((v) => v.name),
        ...campaignRows.map((v) => v.name),
      ];
      const docs = docRows.length ? docRows : docVariables || [];
      return [...ancestorRows, ...collectDocDefaultsAsInherited(docs, taken)];
    }
    const scoped = collectScopedVariables({
      docVariables: [],
      campaign,
      allSprints,
    });
    return scoped
      .filter((v) => !v.shadowed)
      .map((v) => ({
        ...v,
        scope: 'inherited',
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    campaign?.id,
    campaign?.parentSprintId,
    campaign?.variables,
    hasCampaignScope,
    hasDocScope,
    campaignRows,
    docRows,
    docVariables,
    allSprints,
  ]);

  const higherScopeNames = useMemo(
    () => collectHigherScopeNames({ campaign, allSprints }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaign?.id, campaign?.parentSprintId, campaign?.variables, allSprints]
  );

  // This document is the default. This mission is a choice, only while a
  // mission doc is open. Campaign is a choice when this campaign can be written.
  const scopeOptions = useMemo(() => {
    const opts = [];
    if (hasDocScope) {
      opts.push({ value: 'doc', label: DOC_VARIABLE_SOURCE_NAME });
    }
    if (hasMissionScope) {
      opts.push({ value: 'mission', label: MISSION_VARIABLE_SOURCE_NAME });
    }
    if (hasCampaignScope) {
      opts.push({
        value: 'campaign',
        label: campaign?.name ? `Campaign · ${campaign.name}` : 'Campaign',
      });
    }
    return opts;
  }, [hasDocScope, hasMissionScope, hasCampaignScope, campaign?.name]);
  const showScopeSelect = scopeOptions.length > 1;

  const scopedVariables = useMemo(
    () =>
      collectScopedVariables({
        docVariables: hasDocScope ? docRows : docVariables || [],
        campaign,
        allSprints,
        missionVariables,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasDocScope,
      docRows,
      docVariables,
      campaign?.id,
      campaign?.parentSprintId,
      campaign?.variables,
      allSprints,
      missionVariables,
    ]
  );

  const resolveVariable = useCallback(
    (name) => resolveVariableExpanded(scopedVariables, name),
    [scopedVariables]
  );

  const pickerVariables = useMemo(
    () => scopedVariables.filter((v) => !v.shadowed),
    [scopedVariables]
  );

  return {
    inheritedRows,
    higherScopeNames,
    showScopeSelect,
    scopeOptions,
    resolveVariable,
    pickerVariables,
  };
}
